import type { FC, ReactElement } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import {
  ButtonKind,
  ButtonShape,
  ButtonSize,
  ButtonVariant,
  Heading,
  IconButtonLink,
  Text,
} from "@stubhub/broadway-web";
import { ArrowUpIcon } from "@stubhub/broadway-web/icons/functional";
import type { IconProps } from "@stubhub/broadway-web/icons/types";
import type { EventListGridItem } from "src/http/types";
import { useContent } from "src/shared/components/content";
import {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { ResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/ResponsiveCarousel";
import { useTeamThemes } from "src/website-apps/shared/hooks/useTeamThemes";
import { favoriteTeamCarouselStyles } from "./favorite-team-carousel.styles";
import { FavoriteTeamCarouselItem } from "./FavoriteTeamCarouselItem";
import { DEFAULT_FALLBACK_BACKGROUND_COLOR } from "./FavoriteTeamEventCard.styles";
import { getTeamBranding } from "./utils";

const ArrowRightIcon: FC<IconProps> = ({ classes, ...props }) => (
  <ArrowUpIcon {...props} classes={{ ...classes, root: favoriteTeamCarouselStyles.arrowRightIcon }} />
);

interface FavoriteTeamCarouselProps {
  carouselId?: string | number;
  favoriteTeamCategoryId: number;
  events: EventListGridItem[];
  categoryUrl?: string | null | undefined;
}

export const FavoriteTeamCarousel = ({
  carouselId,
  favoriteTeamCategoryId,
  events,
  categoryUrl,
}: FavoriteTeamCarouselProps): ReactElement | null => {
  const favoriteTeamLabel = useContent("cid$#4407461#"); // "Your favorite team"
  const seeAllLabel = useContent("cid$#4407462#"); // "See all events"
  const isMobile = !useMediaQuery(Breakpoint.Tablet);

  const { homeTeamData, awayTeamData } = useTeamThemes(events[0]?.eventMetadata);
  const favoriteTeamData = homeTeamData.categoryId === favoriteTeamCategoryId ? homeTeamData : awayTeamData;
  const teamName = favoriteTeamData.info?.name ?? favoriteTeamData.info?.shortName;
  const branding = getTeamBranding(favoriteTeamData);
  const { logoUrl, teamImageUrl } = branding;
  const logoBadgeBackgroundColor = branding.backgroundColor ?? DEFAULT_FALLBACK_BACKGROUND_COLOR;
  const favoriteTeamAbbreviation = branding.abbreviation ?? teamName?.charAt(0);

  if (events.length === 0) {
    return null;
  }

  return (
    <div>
      <div className={favoriteTeamCarouselStyles.header}>
        {(teamImageUrl || logoUrl || favoriteTeamAbbreviation) && (
          <div
            className={favoriteTeamCarouselStyles.logoBadge}
            data-testid="favorite-team-logo-badge-id"
            style={{ backgroundColor: logoBadgeBackgroundColor }}
          >
            {teamImageUrl ? (
              <img className={favoriteTeamCarouselStyles.teamImage} src={teamImageUrl} alt="" />
            ) : logoUrl ? (
              <img className={favoriteTeamCarouselStyles.logoImage} src={logoUrl} alt="" />
            ) : (
              <span className={favoriteTeamCarouselStyles.fallbackAbbreviation}>{favoriteTeamAbbreviation}</span>
            )}
          </div>
        )}
        <div className={favoriteTeamCarouselStyles.headerText}>
          {favoriteTeamLabel && (
            <Text variant="label" size="small" color="text">
              {favoriteTeamLabel}
            </Text>
          )}
          <Heading className={favoriteTeamCarouselStyles.teamName} variant="title" size="medium">
            {teamName}
          </Heading>
        </div>
        {categoryUrl && seeAllLabel && (
          <IconButtonLink
            className={favoriteTeamCarouselStyles.seeAllLink}
            href={categoryUrl}
            icon={ArrowRightIcon}
            aria-label={seeAllLabel}
            shape={ButtonShape.Round}
            kind={ButtonKind.Neutral}
            variant={ButtonVariant.Ghost}
            size={ButtonSize.Small}
          />
        )}
      </div>
      <ResponsiveCarousel<EventListGridItem>
        carouselId={carouselId}
        analyticsCarouselId={CarouselId.FavoriteTeam}
        analyticsItemType={CarouselItemType.Event}
        gapPx={0}
        hasHoverEffect={false}
        hidePaginationButtons={isMobile}
        useFlex
        items={events}
        renderItem={(event) => (
          <FavoriteTeamCarouselItem key={event.eventId} event={event} favoriteTeamCategoryId={favoriteTeamCategoryId} />
        )}
      />
    </div>
  );
};
